// R17 v2 "best next step" (lead decision L10, M4c; IMPLEMENTATION §0.2 R17, GDD §11.2): a steady-state
// throughput model of the automated factory on the REAL board, and the purchase with the best modelled gain
// per $. It replaced the balance bots' private template model (M4b decision 5): the goal chip and the greedy /
// casual / guided bots read the same ranking (query.upgradeOptions / bestUpgrade); the Gallery and Loader
// cards keep the R17 snapshot walk (rules/bottleneck.ts), which is also the chip's fallback.
//
// The model (integers only; rates are per second × RATE, so "milli-units/s"; deterministic: buildings in
// id order, no Map/Set, ties broken by option order):
//   graph    every non-ghost, unpaused building is a node. Deposits push into their 4 neighbours, machines
//            (processors, Loader, Packer) into their front; a chute chain carries the item to its sink (the
//            chain delivers at most tickHz·min(k, room) / (hop·k) items/s: R8 reservations, M3 #3). A link
//            into a Crate that no chute pulls from, or onto the floor, LEAKS (the items leave the line). A
//            Crate that chutes pull from passes its inflow on (and a stocked Crate supplies its chutes).
//            A loop (Crate ⇄ Crate, Packer ⇄ Crate) keeps only its links that step closer to a Gallery
//            (breakCycles): the model is acyclic and every pass over it is linear in the links.
//   line     only nodes with a path to a Gallery count ("on the line"); before the Loader feeds a Gallery
//            there is no line, no model income and no option (the scripted chips guide the player there).
//   supply   a deposit's drills (hits/s × power ÷ hardness ores/s; manual mining is not automation) × the
//            average units per ore at its level. Its ores are shared round-robin between its acceptors: a
//            water-fill in items where a machine takes up to its cycle rate, a Smelter's fuel link up to the
//            heat its copper needs, and leaks take the rest.
//   machine  items/s = min(input, cycles/s); machines keep the tier, so units/s follow the input's mix. The
//            Smelter also stops at the heat its coal gives (heat per copper unit ÷ heat per coal unit).
//   Loader   laggard-climb (R4): units/s = min(each family's units, cycles/s × units of the highest tier any
//            input reaches); a set forms at the first tier every slot holds (the highest of the slots'
//            lowest tiers) and at bigger common tiers only while the cycle rate cannot carry the units.
//   Packer   repacks to its target (the level cap, R5); the Gallery fires at most tickHz / fireTicks shots
//            per second and pays the permille payout of the ammo tier it fires → the modelled income.
// Value = stageWeight × Σ stages + familyWeight × Σ Loader families + outputWeight × output (economy.json
// advice), every term soft-capped at the units/s the automated-income target needs at the best Packer tier
// × targetMargin. Stages are each deposit's offer to its machines and each machine's capacity, so a step
// inside a tie (copper supply = Smelter = Press) still counts; the output is the modelled income in units of
// the best tier, so the Packer's tier and the gun's fire cap count. Options: the next level of an on-line
// deposit, drill, machine or Packer (L1-equivalent copies at the compiled prices) and a new Drill on a free
// cell beside an on-line deposit, when unlocked and priced. Ranking ("highest Δincome per $"): first the
// options that raise the modelled income, by payback (Δincome × SCORE ÷ price), alone or as one of a pair
// that lifts a tie no single step lifts (two copper drills at L1 = 1 ore/s = a Smelter L2's 1 cycle/s; GDD
// §11.2 "the cheapest bundle that lifts the limiting input"); then the rest by value gain per $ (steps toward
// a later bottleneck); ties by price, then board order. The chip picks the best one the cash affords, else
// the first ("the affordable-or-next purchase", pickBest).

import {
  cycleCost,
  isMatcher,
  isSimpleProc,
  maxLevelOf,
  packerCap,
  recipeOf,
  shopItem,
  slotCap,
  slotFams,
  workPerTick,
} from '../bdefs.ts';
import { cellIndex, DIRS, stepCell } from '../core/grid.ts';
import { floorDiv, mulDivFloor, payoutOf } from '../core/num.ts';
import { cmpInt } from '../core/sort.ts';
import type { Cell, Dir, Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { isActive } from '../defs.ts';
import { depositDef, famDef, findBuilding, itemValue } from '../entities.ts';
import type { BType, FamId, OreId, ShopId } from '../gen/ids.ts';
import { isFree, isOpen, occAt } from '../occupancy.ts';
import type { Building, ProcessorB } from '../state.ts';
import { chuteTakesFrom, topoOf } from '../topology.ts';
import { entrySide } from './accept.ts';
import { familyAccepts } from './place.ts';
import { ownedL1, shopBlock } from './price.ts';

/** Model rates are per second × RATE (fixed point; products stay far below 2^53). */
const RATE = 1000;
/** score = value gain × SCORE ÷ price. */
const SCORE = 1_000_000;
const NONE = Number.MAX_SAFE_INTEGER;
/** Link target: the items leave the line (a Crate nothing pulls from, the floor). */
const LEAK = -1;

/** copy.json keys of the reason line ("Upgrade Lead Vein: lead is your bottleneck"). */
export type AdviceReason = 'goal.whySupply' | 'goal.whyMachine' | 'goal.whyTier' | 'goal.whyPrep';

export interface UpgradeOption {
  /** `level`: one more level merged into building `bid`; `drill`: a new Drill facing deposit `bid`. */
  k: 'level' | 'drill';
  bid: Int;
  /** Type of building `bid` (the deposit for a new Drill). */
  type: BType;
  /** The deposit's ore (deposit level-ups, drills), else null. */
  ore: OreId | null;
  /** The Shop card the copies come from. */
  item: ShopId;
  /** Current level (0 for a new Drill). */
  from: Int;
  /** L1 copies to buy: 2^(from−1) for a level-up, 1 for a new Drill. */
  copies: Int;
  /** Cash for all copies (compiled price table). */
  price: Int;
  /** A new Drill: the free cell beside the deposit and its facing. */
  cell: Cell | null;
  dir: Dir | null;
  /** Modelled automated income gain, milli-$/s. */
  dIncome: Int;
  /** Modelled ammo units/s into the Gallery gain, × RATE. */
  dUnits: Int;
  /** Advice value gain (weighted, soft-capped). */
  gain: Int;
  /** 0 = it raises the modelled income, alone or in a pair (ranked by payback); 1 = a step toward later. */
  tier: Int;
  /** tier 0: Δincome × SCORE ÷ price, the better of alone and its pair (payback); tier 1: gain × SCORE ÷ price. */
  score: Int;
  /**
   * It raises the modelled output or income, alone or as one of a pair that lifts a tie (not only a stage
   * toward a later bottleneck).
   */
  lifts: boolean;
  /**
   * Only when this step is ranked by its pair's payback (a tie it lifts together with another step, better
   * than its own payback): the partner step's building (`pairK` 'level') or the deposit its new Drill faces
   * (`pairK` 'drill'); else both null ("buy together with …").
   */
  pairBid: Int | null;
  pairK: 'level' | 'drill' | null;
  reason: AdviceReason;
  /** The resource the reason names (the deposit's ore family), else null. */
  fam: FamId | null;
}

export interface FactoryEstimate {
  /** Steady-state automated income of the current factory, $/s × 1000. */
  incomeMilli: Int;
  /** Ammo units/s reaching the Galleries, × RATE. */
  unitsMilli: Int;
  /** Ammo tier the (first) Gallery fires in the model (0 = none). */
  tier: Int;
  /** Advice value (weighted, soft-capped). */
  value: Int;
  /** There is a line: some Loader or Packer feeds a Gallery. */
  line: boolean;
}

type Kind = 'deposit' | 'drill' | 'proc' | 'matcher' | 'packer' | 'gallery' | 'crate' | 'other';

interface Link {
  /** Node index, or LEAK. */
  to: Int;
  /** Input slot of the consumer (a Smelter's fuel is its last slot). */
  slot: Int;
  /** Items/s × RATE the link can carry (a chute chain), NONE = no bound. */
  bound: Int;
}

interface MNode {
  b: Building;
  kind: Kind;
  /** Input families by slot (Smelter: [ore, fuel]). */
  ins: FamId[];
  /** Deposits and Crates: every acceptor; machines: the front (0–1 links). */
  outs: Link[];
  /** Drills: the node index of the deposit they face, else −1. */
  faces: Int;
  online: boolean;
  /** Crates: the family it holds / receives, null = unknown. */
  fam: FamId | null;
  /** Crates: units per item (×1000) and the top / lowest tier of its stock, 0 = empty. */
  stockPer: Int;
  stockTop: Int;
  stockLow: Int;
}

interface Factory {
  c: Ctx;
  nodes: MNode[];
  order: Int[];
  line: boolean;
  /** The soft cap of every value term, units/s × RATE (NONE when there is no income target). */
  cap: Int;
  bestTier: Int;
  bestPayout: Int;
}

/** Per-candidate changes to the live board. */
interface Overrides {
  lv: Int[];
  /** L1 drills added per deposit node. */
  extra: Int[];
}

interface Stream {
  items: Int;
  units: Int;
  /** Highest and lowest tier in the stream's mix (0 = nothing). */
  top: Int;
  low: Int;
}

interface Result {
  incomeMilli: Int;
  unitsMilli: Int;
  tier: Int;
  value: Int;
}

function nodeKind(b: Building): Kind {
  if (b.type === 'deposit' || b.type === 'drill' || b.type === 'packer') return b.type;
  if (b.type === 'gallery' || b.type === 'crate') return b.type;
  if (isSimpleProc(b.type)) return 'proc';
  if (isMatcher(b.type)) return 'matcher';
  return 'other';
}

function working(b: Building): boolean {
  return b.ghost === null && !b.paused;
}

function insOf(c: Ctx, b: Building): FamId[] {
  if (b.type === 'packer') return [b.lockFam ?? 'ammo'];
  if (b.type === 'gallery') return ['ammo'];
  if (isSimpleProc(b.type) || isMatcher(b.type)) {
    const fams = slotFams(c.d, b as ProcessorB).slice();
    if (b.type === 'smelter') fams.push(c.d.buildings.smelter.fuel);
    return fams;
  }
  return [];
}

function outFamOf(c: Ctx, b: Building): FamId | null {
  if (b.type === 'packer') return b.lockFam ?? 'ammo';
  if (isSimpleProc(b.type) || isMatcher(b.type)) return recipeOf(c.d, b as ProcessorB).out;
  return null;
}

/** Items a building may hold in transit to it (the R8 reservation room of a chain into it). */
function roomOf(c: Ctx, b: Building): Int {
  const B = c.d.buildings;
  if (b.type === 'gallery') return B.gallery.ammoQueue;
  if (b.type === 'packer') return B.packer.buffer;
  if (b.type === 'crate') return B.crate.capacity[b.level - 1] ?? 1;
  if (isSimpleProc(b.type) || isMatcher(b.type)) return slotCap(c.d, b.type);
  return 1;
}

/** Items/s × RATE a chain of `depth` chutes delivers into a sink with `room` (R8, M3 decision 3). */
function chainBound(c: Ctx, depth: Int, room: Int): Int {
  const k = depth < 1 ? 1 : depth;
  const r = room < k ? room : k;
  return floorDiv(RATE * c.d.tickHz * r, c.d.buildings.chute.hopTicks * k);
}

function indexOfId(c: Ctx, id: Int): Int {
  let lo = 0;
  let hi = c.s.buildings.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const v = (c.s.buildings[mid] as Building).id;
    if (v === id) return mid;
    if (v < id) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}

/** The link from a sink building `to` (entered on `side`, `depth` chutes before it), or null. */
function consumerLink(
  c: Ctx,
  nodes: MNode[],
  to: Building,
  fam: FamId,
  side: Dir,
  depth: Int,
): Link | null {
  if (!working(to) || !familyAccepts(c.d, to, fam, side)) return null;
  const i = indexOfId(c, to.id);
  const n = nodes[i];
  if (n === undefined) return null;
  if (to.type === 'crate') {
    if (to.fam !== null && to.fam !== fam) return null;
    if (n.fam === null) n.fam = fam;
    else if (n.fam !== fam) return null;
  }
  let slot = 0;
  if (n.kind === 'proc' || n.kind === 'matcher') {
    slot = -1;
    for (let k = 0; k < n.ins.length; k++) {
      if (n.ins[k] === fam) {
        slot = k;
        break;
      }
    }
    if (slot < 0) return null;
  } else if (n.kind === 'packer' || n.kind === 'gallery') {
    if (n.ins[0] !== fam) return null;
  } else if (n.kind !== 'crate') {
    return null;
  }
  const bound = depth > 0 ? chainBound(c, depth, roomOf(c, to)) : NONE;
  return { to: i, slot, bound };
}

/**
 * Where an item of `fam` pushed from (fx, fy) into `cell` goes: a consumer link, a leak (floor / a Crate
 * that nothing pulls from is decided later) or null (nothing takes it: rock, a refusing building, a chute
 * that does not take from here or a chain with no sink). Floor cells are leaks only for machines (a
 * deposit's floor neighbours are used only when no acceptor takes the ore).
 */
function linkInto(
  c: Ctx,
  nodes: MNode[],
  fx: Int,
  fy: Int,
  cell: Cell,
  fam: FamId,
  machine: boolean,
): Link | null {
  if (!isOpen(c.occ, cell.x, cell.y)) return null;
  const id = occAt(c.occ, cell.x, cell.y);
  const b = id > 0 ? findBuilding(c.s, id) : null;
  if (b === null) return machine ? { to: LEAK, slot: 0, bound: NONE } : null;
  if (b.type !== 'chute') return consumerLink(c, nodes, b, fam, entrySide(b.x, b.y, fx, fy), 0);
  if (!chuteTakesFrom(b, fx, fy)) return null;
  const topo = topoOf(c);
  const ci = cellIndex(c.occ.cols, b.x, b.y);
  const sink = topo.sink[ci] ?? -1;
  if (sink < 0) return null;
  const depth = topo.depth[ci] ?? 1;
  const sx = sink % c.occ.cols;
  const sy = (sink - sx) / c.occ.cols;
  const sid = occAt(c.occ, sx, sy);
  const sb = sid > 0 ? findBuilding(c.s, sid) : null;
  if (sb === null) return { to: LEAK, slot: 0, bound: chainBound(c, depth, 1) };
  const side = topo.sinkSide[ci];
  return side === null || side === undefined ? null : consumerLink(c, nodes, sb, fam, side, depth);
}

/** The next automated-income target $/s: the current goal's, else the first version-active one. */
function incomeTarget(c: Ctx): Int | null {
  const cur = c.d.unlocks[c.s.progress.goal]?.goal ?? null;
  if (cur !== null && cur.type === 'autoIncome') return cur.perSec;
  for (const u of c.d.unlocks) {
    if (isActive(c.d, u.version) && u.goal !== null && u.goal.type === 'autoIncome')
      return u.goal.perSec;
  }
  return null;
}

function gunMult(c: Ctx, g: Building | null): { mult: Int; fireTicks: Int } | null {
  const tier = g !== null && g.type === 'gallery' && g.gun !== null ? g.gun.tier : 1;
  const gun = c.d.guns[tier - 1];
  return gun === undefined ? null : { mult: gun.multPermille, fireTicks: gun.fireTicks };
}

/** Links from each node to the nearest Gallery (BFS over the reversed links, galleries in index order); NONE = no path. */
function galleryDistance(nodes: readonly MNode[]): Int[] {
  const N = nodes.length;
  const rev: Int[][] = [];
  for (let i = 0; i < N; i++) rev.push([]);
  for (let i = 0; i < N; i++) {
    for (const l of (nodes[i] as MNode).outs) if (l.to >= 0) (rev[l.to] as Int[]).push(i);
  }
  const dist: Int[] = [];
  const queue: Int[] = [];
  for (let i = 0; i < N; i++) {
    const g = (nodes[i] as MNode).kind === 'gallery';
    dist.push(g ? 0 : NONE);
    if (g) queue.push(i);
  }
  for (let q = 0; q < queue.length; q++) {
    const v = queue[q] as Int;
    for (const u of rev[v] as Int[]) {
      if (dist[u] !== NONE) continue;
      dist[u] = (dist[v] as Int) + 1;
      queue.push(u);
    }
  }
  return dist;
}

/**
 * Cycles (a Crate ⇄ Crate chute loop, a Packer ⇄ Crate loop — normal paint strokes can make them): a link that
 * closes a cycle (its target reaches back to its source) is kept only when it steps closer to a Gallery, so
 * the items of a loop circulate toward its exits (a loop carries no net flow in the steady state) and the
 * links become acyclic: every node is evaluated after its suppliers and a Crate's pass-on demand is a finite
 * walk. Every node keeps its shortest path to a Gallery (its first link always steps closer), so `online` is
 * unchanged. O(N · links) once per board (N ≤ the 7×9 cells).
 */
function breakCycles(nodes: MNode[], dist: readonly Int[]): void {
  const N = nodes.length;
  // reach[v][u]: u is reachable from v (v itself included)
  const reach: boolean[][] = [];
  for (let v = 0; v < N; v++) {
    const seen: boolean[] = [];
    for (let i = 0; i < N; i++) seen.push(false);
    seen[v] = true;
    const stack: Int[] = [v];
    while (stack.length > 0) {
      const u = stack.pop() as Int;
      for (const l of (nodes[u] as MNode).outs) {
        if (l.to >= 0 && seen[l.to] !== true) {
          seen[l.to] = true;
          stack.push(l.to);
        }
      }
    }
    reach.push(seen);
  }
  for (let i = 0; i < N; i++) {
    const n = nodes[i] as MNode;
    const kept: Link[] = [];
    for (const l of n.outs) {
      const cyclic = l.to >= 0 && (reach[l.to] as boolean[])[i] === true;
      if (!cyclic || (dist[l.to] as Int) < (dist[i] as Int)) kept.push(l);
    }
    n.outs = kept;
  }
}

function buildFactory(c: Ctx): Factory {
  const bs = c.s.buildings;
  const nodes: MNode[] = [];
  for (const b of bs) {
    nodes.push({
      b,
      kind: working(b) ? nodeKind(b) : 'other',
      ins: working(b) ? insOf(c, b) : [],
      outs: [],
      faces: -1,
      online: false,
      fam: b.type === 'crate' ? b.fam : null,
      stockPer: 0,
      stockTop: 0,
      stockLow: 0,
    });
  }
  // deposits and machines push; drills face a deposit
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i] as MNode;
    const b = n.b;
    if (n.kind === 'deposit' && b.type === 'deposit') {
      const fam = depositDef(c.d, b.ore).fam;
      for (const dir of DIRS) {
        const l = linkInto(c, nodes, b.x, b.y, stepCell(b.x, b.y, dir), fam, false);
        if (l !== null) n.outs.push(l);
      }
    } else if (n.kind === 'drill') {
      const f = stepCell(b.x, b.y, b.dir);
      const id = occAt(c.occ, f.x, f.y);
      const t = id > 0 ? findBuilding(c.s, id) : null;
      if (t !== null && t.type === 'deposit' && working(t)) n.faces = indexOfId(c, t.id);
    } else if (n.kind === 'proc' || n.kind === 'matcher' || n.kind === 'packer') {
      const fam = outFamOf(c, b);
      if (fam === null) continue;
      const l = linkInto(c, nodes, b.x, b.y, stepCell(b.x, b.y, b.dir), fam, true);
      if (l !== null) n.outs.push(l);
    }
  }
  // Crates: chutes pull from them on any side (their family is known once something links into them)
  for (const n of nodes) {
    if (n.kind !== 'crate' || n.b.type !== 'crate') continue;
    const cr = n.b;
    let top = 0;
    let low = 0;
    for (let t = 0; t < cr.counts.length; t++) {
      if ((cr.counts[t] ?? 0) <= 0) continue;
      top = t + 1;
      if (low === 0) low = t + 1;
    }
    if (top > 0) {
      n.stockTop = top;
      n.stockLow = low;
      n.stockPer = (c.d.tierUnits[top - 1] ?? 1) * 1000;
    }
    if (n.fam === null) continue;
    for (const dir of DIRS) {
      const p = stepCell(cr.x, cr.y, dir);
      const id = isOpen(c.occ, p.x, p.y) ? occAt(c.occ, p.x, p.y) : 0;
      const ch = id > 0 ? findBuilding(c.s, id) : null;
      if (ch === null || ch.type !== 'chute' || !chuteTakesFrom(ch, cr.x, cr.y)) continue;
      const l = linkInto(c, nodes, cr.x, cr.y, p, n.fam, false);
      if (l !== null && l.to !== LEAK) n.outs.push(l);
    }
  }
  // on the line: a path to a Gallery (dist = links to the nearest one)
  const dist = galleryDistance(nodes);
  for (let i = 0; i < nodes.length; i++) (nodes[i] as MNode).online = dist[i] !== NONE;
  for (const n of nodes)
    if (n.kind === 'drill' && n.faces >= 0) n.online = (nodes[n.faces] as MNode).online;
  let line = false;
  for (const n of nodes) {
    if ((n.kind === 'matcher' || n.kind === 'packer') && n.online) line = true;
  }
  breakCycles(nodes, dist);
  // evaluation order: Kahn over the (now acyclic) links, the lowest ready index first
  const indeg: Int[] = [];
  for (let i = 0; i < nodes.length; i++) indeg.push(0);
  for (const n of nodes)
    for (const l of n.outs) if (l.to >= 0) indeg[l.to] = (indeg[l.to] ?? 0) + 1;
  const done: boolean[] = [];
  const order: Int[] = [];
  for (let k = 0; k < nodes.length; k++) {
    let pick = -1;
    for (let i = 0; i < nodes.length; i++) {
      if (done[i] !== true && indeg[i] === 0) {
        pick = i;
        break;
      }
    }
    if (pick < 0) break;
    done[pick] = true;
    order.push(pick);
    for (const l of (nodes[pick] as MNode).outs) {
      if (l.to >= 0) indeg[l.to] = (indeg[l.to] ?? 0) - 1;
    }
  }
  // unreachable after breakCycles (kept as a guard: every node is evaluated once)
  for (let i = 0; i < nodes.length; i++) if (done[i] !== true) order.push(i);
  // the soft cap: units/s the income target needs at the best Packer tier (× margin)
  let gallery: Building | null = null;
  for (const n of nodes) {
    if (n.kind === 'gallery' && gallery === null) gallery = n.b;
  }
  const gun = gunMult(c, gallery);
  const caps = c.d.buildings.packer.targetCapByLevel;
  let bestTier = famDef(c.d, 'ammo').maxTier;
  let maxCap = 0;
  for (const x of caps) if (x > maxCap) maxCap = x;
  if (maxCap < bestTier) bestTier = maxCap;
  const bestUnits = c.d.tierUnits[bestTier - 1] ?? 1;
  const bestPayout = gun === null ? 0 : payoutOf(itemValue(c.d, 'ammo', bestTier), gun.mult);
  const target = incomeTarget(c);
  let cap = NONE;
  if (target !== null && bestPayout > 0) {
    const need = floorDiv(target * RATE * bestUnits, bestPayout);
    cap = mulDivFloor(need, c.d.economy.advice.targetMarginPermille, 1000);
  }
  return { c, nodes, order, line, cap, bestTier, bestPayout };
}

function oresPerSec(f: Factory, level: Int, hardMilli: Int): Int {
  const dr = f.c.d.buildings.drill;
  const hit = dr.hitTicks[level - 1];
  const power = dr.powerMilli[level - 1];
  if (hit === undefined || power === undefined) return 0;
  return floorDiv(RATE * f.c.d.tickHz * power, hit * hardMilli);
}

/** Average units per ore ×1000 and the top / lowest tier at a deposit level. */
function depositMix(f: Factory, level: Int): { per: Int; top: Int; low: Int } {
  let per = 0;
  let top = 0;
  let low = 0;
  for (const o of f.c.d.depositRules.oddsByLevel[level - 1] ?? []) {
    per += o.permille * (f.c.d.tierUnits[o.tier - 1] ?? 0);
    if (o.permille > 0 && o.tier > top) top = o.tier;
    if (o.permille > 0 && (low === 0 || o.tier < low)) low = o.tier;
  }
  return { per, top, low };
}

function cyclesPerSec(f: Factory, b: Building, level: Int): Int {
  if (level <= 0 || !(isSimpleProc(b.type) || isMatcher(b.type))) return 0;
  return floorDiv(RATE * f.c.d.tickHz * workPerTick(f.c.d, level), cycleCost(f.c.d, b.type));
}

/**
 * Round-robin share of `supply` items between links with `demand` (NONE = takes anything): a water-fill,
 * the smallest demand first (link order breaks ties). Returns the items per link.
 */
function waterFill(supply: Int, demand: readonly Int[]): Int[] {
  const n = demand.length;
  const idx: Int[] = [];
  for (let i = 0; i < n; i++) {
    let j = idx.length;
    while (j > 0 && cmpInt(demand[idx[j - 1] as Int] as Int, demand[i] as Int) > 0) j--;
    idx.splice(j, 0, i);
  }
  const out: Int[] = [];
  for (let i = 0; i < n; i++) out.push(0);
  let left = supply;
  for (let k = 0; k < n; k++) {
    const i = idx[k] as Int;
    const share = floorDiv(left, n - k);
    const d = demand[i] as Int;
    const a = d < share ? d : share;
    out[i] = a;
    left -= a;
  }
  return out;
}

function soft(f: Factory, x: Int): Int {
  return x < f.cap ? x : f.cap;
}

function scaleUnits(units: Int, part: Int, whole: Int): Int {
  return whole > 0 ? mulDivFloor(units, part, whole) : 0;
}

/** Evaluates the factory with `ov`. */
function evaluate(f: Factory, ov: Overrides): Result {
  const { c, nodes } = f;
  const d = c.d;
  const N = nodes.length;
  const inItems: Int[][] = [];
  const inUnits: Int[][] = [];
  const inTop: Int[][] = [];
  const inLow: Int[][] = [];
  const nomPer: Int[] = [];
  const nomTop: Int[] = [];
  const fuelNeed: Int[] = [];
  for (let i = 0; i < N; i++) fuelNeed.push(NONE);
  let result: Result = { incomeMilli: 0, unitsMilli: 0, tier: 0, value: 0 };
  // two rounds: the second uses the fuel each Smelter's copper actually needs
  for (let round = 0; round < 2; round++) {
    inItems.length = 0;
    inUnits.length = 0;
    inTop.length = 0;
    inLow.length = 0;
    nomPer.length = 0;
    nomTop.length = 0;
    for (let i = 0; i < N; i++) {
      const k = (nodes[i] as MNode).ins.length || 1;
      const zi: Int[] = [];
      const zu: Int[] = [];
      const zt: Int[] = [];
      for (let s = 0; s < k; s++) {
        zi.push(0);
        zu.push(0);
        zt.push(0);
      }
      inItems.push(zi);
      inUnits.push(zu);
      inTop.push(zt);
      inLow.push(zt.slice());
      nomPer.push(0);
      nomTop.push(0);
    }
    const push = (l: Link, st: Stream, per: Int, top: Int): void => {
      if (l.to < 0) return;
      const items = st.items < l.bound ? st.items : l.bound;
      const units = scaleUnits(st.units, items, st.items);
      const ii = inItems[l.to] as Int[];
      ii[l.slot] = (ii[l.slot] ?? 0) + items;
      const uu = inUnits[l.to] as Int[];
      uu[l.slot] = (uu[l.slot] ?? 0) + units;
      const tt = inTop[l.to] as Int[];
      if (items > 0 && st.top > (tt[l.slot] ?? 0)) tt[l.slot] = st.top;
      const ll = inLow[l.to] as Int[];
      const lo = ll[l.slot] ?? 0;
      if (items > 0 && st.low > 0 && (lo === 0 || st.low < lo)) ll[l.slot] = st.low;
      if (per > (nomPer[l.to] ?? 0)) nomPer[l.to] = per;
      if (top > (nomTop[l.to] ?? 0)) nomTop[l.to] = top;
    };
    // a Crate's pass-on demand, worked out once per round and units-per-item (−1 = not yet)
    const crateDem: Int[] = [];
    const crateDemPer: Int[] = [];
    for (let i = 0; i < N; i++) {
      crateDem.push(-1);
      crateDemPer.push(0);
    }
    /**
     * Items/s a consumer link takes: its machine's cycle rate, a Smelter's fuel need, what a Crate passes on
     * (anything when nothing pulls from it), anything for the other sinks; never more than the link carries.
     * The links are acyclic (breakCycles) and a Crate's demand is memoised, so this is linear in the links.
     */
    const demandOf = (l: Link, per: Int): Int => {
      if (l.to < 0) return l.bound;
      const n = nodes[l.to] as MNode;
      let dm = NONE;
      if (n.kind === 'proc') {
        if (n.b.type === 'smelter' && l.slot === n.ins.length - 1) {
          // the heat need in items, rounded up (a floored demand would starve the tank by a rounding step)
          const need = fuelNeed[l.to] ?? NONE;
          dm = need === NONE || per <= 0 ? NONE : floorDiv(need * 1000 + per - 1, per);
        } else {
          dm = cyclesPerSec(f, n.b, ov.lv[l.to] ?? 0);
        }
      } else if (n.kind === 'crate' && n.outs.length > 0) {
        const known = crateDem[l.to] as Int;
        if (known >= 0 && crateDemPer[l.to] === per) {
          dm = known;
        } else {
          // 0 while in progress: a guard only (no link leads back here)
          crateDem[l.to] = 0;
          crateDemPer[l.to] = per;
          let sum = 0;
          for (const o of n.outs) {
            const x = demandOf(o, per);
            if (x === NONE) {
              sum = NONE;
              break;
            }
            sum += x;
          }
          crateDem[l.to] = sum;
          crateDemPer[l.to] = per;
          dm = sum;
        }
      }
      return dm < l.bound ? dm : l.bound;
    };
    let stages = 0;
    let families = 0;
    let income = 0;
    let unitsIn = 0;
    let tierFired = 0;
    for (const i of f.order) {
      const n = nodes[i] as MNode;
      const b = n.b;
      const lv = ov.lv[i] ?? 0;
      if (n.kind === 'deposit' && b.type === 'deposit') {
        const dep = depositDef(d, b.ore);
        let ores = 0;
        for (let j = 0; j < N; j++) {
          const dn = nodes[j] as MNode;
          if (dn.kind === 'drill' && dn.faces === i)
            ores += oresPerSec(f, ov.lv[j] ?? 0, dep.hardMilli);
        }
        ores += (ov.extra[i] ?? 0) * oresPerSec(f, 1, dep.hardMilli);
        const mix = depositMix(f, lv);
        const st: Stream = {
          items: ores,
          units: floorDiv(ores * mix.per, 1000),
          top: mix.top,
          low: mix.low,
        };
        const dem: Int[] = [];
        const offer: Int[] = [];
        for (const l of n.outs) {
          dem.push(demandOf(l, mix.per));
          const main =
            l.to >= 0 &&
            (nodes[l.to] as MNode).kind === 'proc' &&
            !(
              (nodes[l.to] as MNode).b.type === 'smelter' &&
              l.slot === (nodes[l.to] as MNode).ins.length - 1
            );
          offer.push(main ? l.bound : demandOf(l, mix.per));
        }
        const alloc = waterFill(st.items, dem);
        for (let k = 0; k < n.outs.length; k++) {
          const a = alloc[k] as Int;
          const part: Stream = {
            items: a,
            units: floorDiv(a * mix.per, 1000),
            top: mix.top,
            low: mix.low,
          };
          push(n.outs[k] as Link, part, mix.per, mix.top);
        }
        if (n.online) {
          // the stage: what the deposit offers its on-line machines (their caps ignored, leaks and fuel taken)
          const off = waterFill(st.items, offer);
          let main = 0;
          for (let k = 0; k < n.outs.length; k++) {
            const l = n.outs[k] as Link;
            if (l.to < 0 || !(nodes[l.to] as MNode).online) continue;
            const t = nodes[l.to] as MNode;
            if (t.kind !== 'proc' || (t.b.type === 'smelter' && l.slot === t.ins.length - 1))
              continue;
            main += off[k] as Int;
          }
          stages += soft(f, floorDiv(main * mix.per, 1000));
        }
      } else if (n.kind === 'crate') {
        let items = (inItems[i] as Int[])[0] ?? 0;
        let units = (inUnits[i] as Int[])[0] ?? 0;
        let top = (inTop[i] as Int[])[0] ?? 0;
        let low = (inLow[i] as Int[])[0] ?? 0;
        let per = items > 0 ? floorDiv(units * 1000, items) : (nomPer[i] ?? 0);
        if (n.stockTop > 0) {
          items = NONE;
          per = n.stockPer;
          units = NONE;
          if (n.stockTop > top) top = n.stockTop;
          if (low === 0 || n.stockLow < low) low = n.stockLow;
        }
        if (n.outs.length === 0) continue;
        const dem: Int[] = [];
        for (const l of n.outs) dem.push(demandOf(l, per));
        const alloc = waterFill(items, dem);
        for (let k = 0; k < n.outs.length; k++) {
          const a = alloc[k] as Int;
          const u = units === NONE ? floorDiv(a * per, 1000) : scaleUnits(units, a, items);
          push(n.outs[k] as Link, { items: a, units: u, top, low }, per, top);
        }
      } else if (n.kind === 'proc') {
        const cyc = cyclesPerSec(f, b, lv);
        const ii = (inItems[i] as Int[])[0] ?? 0;
        const iu = (inUnits[i] as Int[])[0] ?? 0;
        const top = (inTop[i] as Int[])[0] ?? 0;
        const low = (inLow[i] as Int[])[0] ?? 0;
        let items = ii < cyc ? ii : cyc;
        if (b.type === 'smelter' && ii > 0) {
          // heat: coal units × heat per coal unit ÷ heat per copper unit
          const fuelUnits = (inUnits[i] as Int[])[n.ins.length - 1] ?? 0;
          const fuel = famDef(d, d.buildings.smelter.fuel).fuelHeat[0] ?? 1;
          const need = recipeOf(d, b as ProcessorB).heatNeed[0] ?? 0;
          if (need > 0) {
            const cuUnits = floorDiv(fuelUnits * fuel, need);
            const cuItems = iu > 0 ? mulDivFloor(cuUnits, ii, iu) : 0;
            if (cuItems < items) items = cuItems;
            // the fuel the next round offers: the copper this Smelter can pass on (its consumer's rate too)
            let used = ii < cyc ? ii : cyc;
            const out = n.outs[0];
            if (out !== undefined && out.to >= 0) {
              const t = nodes[out.to] as MNode;
              const tc = t.kind === 'proc' ? cyclesPerSec(f, t.b, ov.lv[out.to] ?? 0) : NONE;
              if (tc < used) used = tc;
            }
            const cu = scaleUnits(iu, used, ii);
            fuelNeed[i] = floorDiv(cu * need + fuel - 1, fuel);
          }
        }
        const units = scaleUnits(iu, items, ii);
        const per = ii > 0 ? floorDiv(iu * 1000, ii) : (nomPer[i] ?? 0) || 1000;
        const out = n.outs[0];
        if (out !== undefined) {
          push(out, { items, units, top, low }, per, top > 0 ? top : (nomTop[i] ?? 0));
        }
        if (n.online) stages += soft(f, floorDiv(cyc * per, 1000));
      } else if (n.kind === 'matcher') {
        // laggard-climb (R4): a set forms at the first tier every slot holds — the highest of the slots'
        // lowest tiers (a laggard climbs to it) — and at bigger common tiers only while the cycle rate
        // cannot carry the units at that tier (full slots then hold the bigger items), up to the highest
        // tier any input reaches. A backed-up Loader holds its finished set (R2) and does not climb.
        const cyc = cyclesPerSec(f, b, lv);
        const us = inUnits[i] as Int[];
        const ts = inTop[i] as Int[];
        const ls = inLow[i] as Int[];
        let kCap = 0;
        let kSet = 0;
        let minUnits = NONE;
        for (let s = 0; s < n.ins.length; s++) {
          const t = ts[s] ?? 0;
          if (t > kCap) kCap = t;
          const lo = ls[s] ?? 0;
          if (lo > kSet) kSet = lo;
          const u = us[s] ?? 0;
          if (u < minUnits) minUnits = u;
          if (n.online) families += soft(f, u);
        }
        if (kCap === 0) kCap = (nomTop[i] ?? 0) || 1;
        if (kSet === 0 || kSet > kCap) kSet = kCap;
        const capUnits = cyc * (d.tierUnits[kCap - 1] ?? 1);
        const units = minUnits === NONE ? 0 : minUnits < capUnits ? minUnits : capUnits;
        let k = kSet;
        while (k < kCap && cyc * (d.tierUnits[k - 1] ?? 1) < units) k++;
        const perSet = d.tierUnits[k - 1] ?? 1;
        const items = floorDiv(units, perSet);
        const out = n.outs[0];
        if (out !== undefined) push(out, { items, units, top: k, low: k }, perSet * 1000, k);
        if (n.online) stages += soft(f, capUnits);
      } else if (n.kind === 'packer' && b.type === 'packer') {
        const iu = (inUnits[i] as Int[])[0] ?? 0;
        const top = (inTop[i] as Int[])[0] ?? 0;
        const tgt = packerTarget(f, b, lv);
        const tier = top > tgt ? top : tgt;
        const per = d.tierUnits[tier - 1] ?? 1;
        const out = n.outs[0];
        if (out !== undefined) {
          push(
            out,
            { items: floorDiv(iu, per), units: iu, top: tier, low: tier },
            per * 1000,
            tier,
          );
        }
      } else if (n.kind === 'gallery' && b.type === 'gallery') {
        const ii = (inItems[i] as Int[])[0] ?? 0;
        const iu = (inUnits[i] as Int[])[0] ?? 0;
        const tier = (inTop[i] as Int[])[0] ?? 0;
        unitsIn += iu;
        if (b.gun === null || tier <= 0) continue;
        const gun = d.guns[b.gun.tier - 1];
        if (gun === undefined) continue;
        const fire = floorDiv(RATE * d.tickHz, gun.fireTicks);
        const shots = ii < fire ? ii : fire;
        income += shots * payoutOf(itemValue(d, 'ammo', tier), gun.multPermille);
        if (tierFired === 0) tierFired = tier;
      }
    }
    // the output term: income in units of the best tier (so the Packer's tier and the fire cap count)
    const out =
      f.bestPayout > 0 ? mulDivFloor(income, d.tierUnits[f.bestTier - 1] ?? 1, f.bestPayout) : 0;
    const a = d.economy.advice;
    result = {
      incomeMilli: income,
      unitsMilli: unitsIn,
      tier: tierFired,
      value: a.stageWeight * stages + a.familyWeight * families + a.outputWeight * soft(f, out),
    };
  }
  return result;
}

/** R5: a Packer whose target is the level cap follows the cap on a level-up. */
function packerTarget(f: Factory, b: Building, level: Int): Int {
  if (b.type !== 'packer') return 1;
  const fam = b.lockFam;
  if (level === b.level) return b.target;
  return b.target === packerCap(f.c.d, b.level, fam) ? packerCap(f.c.d, level, fam) : b.target;
}

function baseOverrides(f: Factory): Overrides {
  const lv: Int[] = [];
  const extra: Int[] = [];
  for (const n of f.nodes) {
    lv.push(n.kind === 'other' ? 0 : n.b.level);
    extra.push(0);
  }
  return { lv, extra };
}

/** Cash for `copies` more L1-equivalents of a shop item (null: past the table / flat). */
function copiesPrice(c: Ctx, item: ShopId, copies: Int): Int | null {
  const def = shopItem(c.d, item);
  if (def.flat) return null;
  let n = ownedL1(c.s, def) - def.freeCopies;
  if (n < 0) n = 0;
  let sum = 0;
  for (let k = 0; k < copies; k++) {
    const p = def.prices[n + k];
    if (p === undefined) return null;
    sum += p;
  }
  return sum;
}

function shopOf(b: Building): ShopId {
  return (b.type === 'deposit' ? `deposit.${b.ore}` : b.type) as ShopId;
}

function reasonOf(n: MNode, lifts: boolean): AdviceReason {
  if (!lifts) return 'goal.whyPrep';
  if (n.kind === 'packer') return 'goal.whyTier';
  if (n.kind === 'deposit' || n.kind === 'drill') return 'goal.whySupply';
  return 'goal.whyMachine';
}

/** Ranking: income-raising options first, then score desc, then price asc, then option order. */
function better(a: UpgradeOption, b: UpgradeOption): boolean {
  const t = cmpInt(a.tier, b.tier);
  if (t !== 0) return t < 0;
  const s = cmpInt(a.score, b.score);
  if (s !== 0) return s > 0;
  return cmpInt(a.price, b.price) < 0;
}

export function factoryEstimate(c: Ctx): FactoryEstimate {
  const f = buildFactory(c);
  const r = evaluate(f, baseOverrides(f));
  return { ...r, line: f.line };
}

interface Cand {
  /** Node index of the building levelled up / the deposit a new Drill faces. */
  i: Int;
  k: 'level' | 'drill';
  item: ShopId;
  from: Int;
  copies: Int;
  cell: Cell | null;
  dir: Dir | null;
  price: Int;
  gain: Int;
  dIncome: Int;
  dUnits: Int;
  /** Best payback of a pair that lifts more than its two steps alone (a tie): partner index, Δincome, price. */
  pair: Int;
  pairIncome: Int;
  pairPrice: Int;
}

/** The board with candidates `cs` applied. */
function withCands(base: Overrides, cs: readonly Cand[]): Overrides {
  const lv = base.lv.slice();
  const extra = base.extra.slice();
  for (const x of cs) {
    if (x.k === 'level') lv[x.i] = (base.lv[x.i] ?? 0) + 1;
    else extra[x.i] = (extra[x.i] ?? 0) + 1;
  }
  return { lv, extra };
}

/**
 * Every priced, unlocked option with a positive value gain, best first. Ties (e.g. two copper drills at L1
 * = 1 ore/s = a Smelter L2's 1 cycle/s) are lifted by no single step: every pair of options is evaluated
 * too, and a pair that raises the income by more than its two steps alone ranks both steps by the pair's
 * payback (GDD §11.2 "the cheapest bundle that lifts the limiting input"; bundles of two).
 */
export function upgradeOptions(c: Ctx): UpgradeOption[] {
  const f = buildFactory(c);
  if (!f.line) return [];
  const base = baseOverrides(f);
  const r0 = evaluate(f, base);
  const cands: Cand[] = [];
  const consider = (
    i: Int,
    k: 'level' | 'drill',
    item: ShopId,
    from: Int,
    copies: Int,
    cell: Cell | null,
    dir: Dir | null,
  ): void => {
    if (shopBlock(c.s, c.d, item) !== null) return;
    const price = copiesPrice(c, item, copies);
    if (price === null || price <= 0) return;
    const x: Cand = {
      i,
      k,
      item,
      from,
      copies,
      cell,
      dir,
      price,
      gain: 0,
      dIncome: 0,
      dUnits: 0,
      pair: -1,
      pairIncome: 0,
      pairPrice: 0,
    };
    const r = evaluate(f, withCands(base, [x]));
    x.gain = r.value - r0.value;
    if (x.gain <= 0) return;
    x.dIncome = r.incomeMilli - r0.incomeMilli;
    x.dUnits = r.unitsMilli - r0.unitsMilli;
    cands.push(x);
  };
  for (let i = 0; i < f.nodes.length; i++) {
    const n = f.nodes[i] as MNode;
    if (!n.online || n.kind === 'gallery' || n.kind === 'crate' || n.kind === 'other') continue;
    const b = n.b;
    const lv = base.lv[i] ?? 0;
    if (lv >= 1 && lv < maxLevelOf(c.d, b)) {
      const ok =
        b.type !== 'packer' || packerCap(c.d, lv + 1, b.lockFam) > packerCap(c.d, lv, b.lockFam);
      let copies = 1;
      for (let l = 1; l < lv; l++) copies *= 2;
      if (ok) consider(i, 'level', shopOf(b), lv, copies, null, null);
    }
    if (n.kind === 'deposit') {
      // a new Drill on the first free cell beside the deposit (N/E/S/W), facing it
      for (const dir of DIRS) {
        const p = stepCell(b.x, b.y, dir);
        if (!isOpen(c.occ, p.x, p.y) || !isFree(c.occ, p.x, p.y)) continue;
        const facing: Dir = dir === 'N' ? 'S' : dir === 'S' ? 'N' : dir === 'E' ? 'W' : 'E';
        consider(i, 'drill', 'drill' as ShopId, 0, 1, p, facing);
        break;
      }
    }
  }
  // pairs: a bundle of two steps that lifts more than the two alone
  for (let a = 0; a < cands.length; a++) {
    for (let b = a + 1; b < cands.length; b++) {
      const x = cands[a] as Cand;
      const y = cands[b] as Cand;
      const r = evaluate(f, withCands(base, [x, y]));
      const dInc = r.incomeMilli - r0.incomeMilli;
      // a synergy: the pair lifts more than its two steps alone (a step that lowers the income alone counts 0)
      const alone = (x.dIncome > 0 ? x.dIncome : 0) + (y.dIncome > 0 ? y.dIncome : 0);
      if (dInc <= 0 || dInc <= alone) continue;
      const price =
        x.item === y.item ? copiesPrice(c, x.item, x.copies + y.copies) : x.price + y.price;
      if (price === null) continue;
      const score = floorDiv(dInc * SCORE, price);
      for (const [m, o] of [
        [x, b],
        [y, a],
      ] as const) {
        if (m.pair < 0 || score > floorDiv(m.pairIncome * SCORE, m.pairPrice)) {
          m.pair = o;
          m.pairIncome = dInc;
          m.pairPrice = price;
        }
      }
    }
  }
  const out: UpgradeOption[] = [];
  for (const x of cands) {
    const n = f.nodes[x.i] as MNode;
    const dep = n.kind === 'drill' ? (f.nodes[n.faces] as MNode) : n;
    const single = x.dIncome > 0 ? floorDiv(x.dIncome * SCORE, x.price) : 0;
    const paired = x.pair >= 0 ? floorDiv(x.pairIncome * SCORE, x.pairPrice) : 0;
    const tier = single > 0 || paired > 0 ? 0 : 1;
    const partner = x.pair >= 0 && paired > single ? (cands[x.pair] as Cand) : null;
    const o: UpgradeOption = {
      k: x.k,
      bid: n.b.id,
      type: n.b.type,
      ore: dep.b.type === 'deposit' ? dep.b.ore : null,
      item: x.item,
      from: x.from,
      copies: x.copies,
      price: x.price,
      cell: x.cell,
      dir: x.dir,
      dIncome: x.dIncome,
      dUnits: x.dUnits,
      gain: x.gain,
      tier,
      score: tier === 0 ? (single > paired ? single : paired) : floorDiv(x.gain * SCORE, x.price),
      lifts: x.dIncome > 0 || x.dUnits > 0 || x.pair >= 0,
      pairBid: partner === null ? null : (f.nodes[partner.i] as MNode).b.id,
      pairK: partner === null ? null : partner.k,
      reason: reasonOf(x.k === 'drill' ? dep : n, tier === 0),
      fam: dep.b.type === 'deposit' ? depositDef(c.d, dep.b.ore).fam : null,
    };
    let j = out.length;
    while (j > 0 && better(o, out[j - 1] as UpgradeOption)) j--;
    out.splice(j, 0, o);
  }
  return out;
}

/**
 * A key of everything `upgradeOptions` reads besides cash (the UI snapshot caches the ranking under it,
 * game/snapshot.ts): the mask, the unlocks and the goal (shop blocks, the income target), and per building its
 * id, type, level, cell, facing, pause / ghost state and configuration (ore, chute entry, mode, Packer target
 * and lock), a Crate's family and stocked tier span (stockLow / stockTop), the Gallery's gun tier, and the
 * open / free state of the four cells beside each deposit (the new-Drill cell; a loose item there moves it).
 * Loose items elsewhere, cash and ticks do not change the ranking. A state field the model starts to read
 * must be added here (tests/game/controller.test.ts compares the cached advice with a fresh ranking).
 */
export function adviceInputsKey(c: Ctx): string {
  const s = c.s;
  const parts: string[] = [`${s.bounds}`, s.progress.unlocked.join(','), `${s.progress.goal}`];
  for (const b of s.buildings) {
    let extra = '';
    switch (b.type) {
      case 'deposit':
        extra = b.ore;
        for (const dir of DIRS) {
          const p = stepCell(b.x, b.y, dir);
          extra += isOpen(c.occ, p.x, p.y) && isFree(c.occ, p.x, p.y) ? '1' : '0';
        }
        break;
      case 'crate': {
        let low = 0;
        let top = 0;
        for (let t = 0; t < b.counts.length; t++) {
          if ((b.counts[t] ?? 0) <= 0) continue;
          top = t + 1;
          if (low === 0) low = t + 1;
        }
        extra = `${b.fam ?? ''}:${low}:${top}`;
        break;
      }
      case 'packer':
        extra = `${b.target}:${b.lockFam ?? ''}`;
        break;
      case 'chute':
        extra = b.inDir;
        break;
      case 'gallery':
        extra = `${b.gun === null ? 0 : b.gun.tier}`;
        break;
      case 'drill':
      case 'dock':
        break;
      default:
        extra = b.mode ?? '';
        break;
    }
    parts.push(
      `${b.id}:${b.type}:${b.level}:${b.x},${b.y}:${b.dir}:${b.paused ? 1 : 0}:${b.ghost !== null ? 1 : 0}:${extra}`,
    );
  }
  return parts.join('|');
}

/**
 * The chip's pick from ranked options ("the affordable-or-next purchase"): the best option of the top rank
 * tier that `cash` affords now, else the first option (the next one to save for); null when there is none.
 */
export function pickBest(opts: readonly UpgradeOption[], cash: Int): UpgradeOption | null {
  const first = opts[0];
  if (first === undefined) return null;
  for (const o of opts) {
    if (o.tier !== first.tier) break;
    if (o.price <= cash) return o;
  }
  return first;
}

/** The goal chip's best next step (`pickBest` of `upgradeOptions` at the current cash), or null. */
export function bestUpgrade(c: Ctx): UpgradeOption | null {
  return pickBest(upgradeOptions(c), c.s.wallet.cash);
}
